import { authConfigFor } from '../auth/config';
import { clearSession, getAccessToken } from '../auth/session';
import type {
  AdminRequest,
  AdminResponse,
  ErrorResponse,
  MyGrantResponse,
  TenantUserResponse,
  OfferingRequest,
  OfferingResponse,
  PaymentConfigRequest,
  PaymentConfigResponse,
  SsoConfigRequest,
  SsoConfigResponse,
  TenantRequest,
  TenantResponse,
  VersionResponse,
} from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/**
 * fetch with the platform bearer token attached when signed in (ADR-017); a plain
 * pass-through when signed out (local runs against an unenforced backend). A 401 means the
 * token expired or was revoked: the session is cleared so the shell falls back to the
 * sign-in view, and the ApiError still surfaces to the caller.
 */
async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const token = getAccessToken();
  let res: Response;
  if (token === null) {
    res = init === undefined ? await fetch(input) : await fetch(input, init);
  } else {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);
    res = await fetch(input, { ...init, headers });
  }
  if (res.status === 401) clearSession();
  return res;
}

/** Thrown for any non-2xx response; carries the backend's domain error contract. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ErrorResponse,
  ) {
    super(`${body.code}: ${body.description}`);
  }
}

const offeringsPath = (tenant: string): string => `/tenants/${encodeURIComponent(tenant)}/offerings`;
const offeringPath = (tenant: string, accessId: string): string =>
  `${offeringsPath(tenant)}/${encodeURIComponent(accessId)}`;

/** Throws an {@link ApiError} (with the parsed domain error) for any non-2xx response. */
async function ensureOk(res: Response): Promise<Response> {
  if (!res.ok) throw new ApiError(res.status, (await res.json()) as ErrorResponse);
  return res;
}

/** GET /api/tenants/{tenant}/offerings — every offering for the tenant (read API). */
export async function listOfferings(tenant: string): Promise<OfferingResponse[]> {
  const res = await ensureOk(await apiFetch(`${BASE}${offeringsPath(tenant)}`));
  return (await res.json()) as OfferingResponse[];
}

/** GET /api/tenants/{tenant}/offerings/{accessId} — one offering, or ApiError(404) (read API). */
export async function getOffering(tenant: string, accessId: string): Promise<OfferingResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}${offeringPath(tenant, accessId)}`));
  return (await res.json()) as OfferingResponse;
}

/** PUT /api/tenants/{tenant}/offerings/{accessId} — create or replace an offering (write API). */
export async function putOffering(
  tenant: string,
  accessId: string,
  offering: OfferingRequest,
): Promise<OfferingResponse> {
  const res = await ensureOk(
    await apiFetch(`${BASE}${offeringPath(tenant, accessId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(offering),
    }),
  );
  return (await res.json()) as OfferingResponse;
}

/** DELETE /api/tenants/{tenant}/offerings/{accessId} — remove an offering, 204 (write API). */
export async function deleteOffering(tenant: string, accessId: string): Promise<void> {
  await ensureOk(await apiFetch(`${BASE}${offeringPath(tenant, accessId)}`, { method: 'DELETE' }));
}

const tenantsPath = '/tenants';
const tenantPath = (tenant: string): string => `${tenantsPath}/${encodeURIComponent(tenant)}`;

/** GET /api/tenants — every tenant in the directory (admin read API). */
export async function listTenants(): Promise<TenantResponse[]> {
  const res = await ensureOk(await apiFetch(`${BASE}${tenantsPath}`));
  return (await res.json()) as TenantResponse[];
}

/** GET /api/tenants/{tenant} — one tenant, or ApiError(404). */
export async function getTenant(tenant: string): Promise<TenantResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}${tenantPath(tenant)}`));
  return (await res.json()) as TenantResponse;
}

/** PUT /api/tenants/{tenant} — create or replace a tenant (write API). */
export async function putTenant(tenant: string, body: TenantRequest): Promise<TenantResponse> {
  const res = await ensureOk(
    await apiFetch(`${BASE}${tenantPath(tenant)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
  return (await res.json()) as TenantResponse;
}

/** DELETE /api/tenants/{tenant} — remove a tenant, 204 (or ApiError(409) for the internal tenant). */
export async function deleteTenant(tenant: string): Promise<void> {
  await ensureOk(await apiFetch(`${BASE}${tenantPath(tenant)}`, { method: 'DELETE' }));
}

/** GET /api/internal-tenant — the designated internal/admin tenant, or ApiError(404) when none. */
export async function getInternalTenant(): Promise<TenantResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/internal-tenant`));
  return (await res.json()) as TenantResponse;
}

const adminsPath = (tenant: string): string => `${tenantPath(tenant)}/admins`;
const adminPath = (tenant: string, email: string): string =>
  `${adminsPath(tenant)}/${encodeURIComponent(email)}`;

/** GET /api/tenants/{tenant}/admins — every admin granted on the tenant (admin read API). */
export async function listAdmins(tenant: string): Promise<AdminResponse[]> {
  const res = await ensureOk(await apiFetch(`${BASE}${adminsPath(tenant)}`));
  return (await res.json()) as AdminResponse[];
}

/** GET /api/tenants/{tenant}/admins/{email} — one admin, or ApiError(404) (read API). */
export async function getAdmin(tenant: string, email: string): Promise<AdminResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}${adminPath(tenant, email)}`));
  return (await res.json()) as AdminResponse;
}

/** PUT /api/tenants/{tenant}/admins/{email} — grant or replace an admin (write API). */
export async function grantAdmin(
  tenant: string,
  email: string,
  body: AdminRequest,
): Promise<AdminResponse> {
  const res = await ensureOk(
    await apiFetch(`${BASE}${adminPath(tenant, email)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
  return (await res.json()) as AdminResponse;
}

/** DELETE /api/tenants/{tenant}/admins/{email} — revoke an admin, 204 (write API). */
export async function deleteAdmin(tenant: string, email: string): Promise<void> {
  await ensureOk(await apiFetch(`${BASE}${adminPath(tenant, email)}`, { method: 'DELETE' }));
}

/** GET /api/my-grants — every tenant the signed-in account administers, with role + display name. */
export async function listMyGrants(): Promise<MyGrantResponse[]> {
  const res = await ensureOk(await apiFetch(`${BASE}/my-grants`));
  return (await res.json()) as MyGrantResponse[];
}

const ssoPath = (tenant: string): string => `${tenantPath(tenant)}/sso`;

/** GET /api/tenants/{tenant}/sso — the tenant's SSO config, or ApiError(404) when unconfigured. */
export async function getSsoConfig(tenant: string): Promise<SsoConfigResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}${ssoPath(tenant)}`));
  return (await res.json()) as SsoConfigResponse;
}

/** PUT /api/tenants/{tenant}/sso — create or replace the tenant's SSO config (write API). */
export async function putSsoConfig(
  tenant: string,
  body: SsoConfigRequest,
): Promise<SsoConfigResponse> {
  const res = await ensureOk(
    await apiFetch(`${BASE}${ssoPath(tenant)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
  return (await res.json()) as SsoConfigResponse;
}

/** DELETE /api/tenants/{tenant}/sso — remove the tenant's SSO config, 204 (write API, idempotent). */
export async function deleteSsoConfig(tenant: string): Promise<void> {
  await ensureOk(await apiFetch(`${BASE}${ssoPath(tenant)}`, { method: 'DELETE' }));
}

const paymentPath = (tenant: string): string => `${tenantPath(tenant)}/payment`;

/** GET /api/tenants/{tenant}/payment — the tenant's payment config, or ApiError(404) when unconfigured. */
export async function getPaymentConfig(tenant: string): Promise<PaymentConfigResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}${paymentPath(tenant)}`));
  return (await res.json()) as PaymentConfigResponse;
}

/** PUT /api/tenants/{tenant}/payment — create or replace the tenant's payment config (write API). */
export async function putPaymentConfig(
  tenant: string,
  body: PaymentConfigRequest,
): Promise<PaymentConfigResponse> {
  const res = await ensureOk(
    await apiFetch(`${BASE}${paymentPath(tenant)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
  return (await res.json()) as PaymentConfigResponse;
}

/** DELETE /api/tenants/{tenant}/payment — remove the tenant's payment config, 204 (write API, idempotent). */
export async function deletePaymentConfig(tenant: string): Promise<void> {
  await ensureOk(await apiFetch(`${BASE}${paymentPath(tenant)}`, { method: 'DELETE' }));
}

/**
 * POST {issuer}/tenants/{tenant}/users — the tenant pool's users (ADR-024 §3), served by the
 * AUTH SERVER because it owns the user store. Cross-origin as a SIMPLE request, like the /par
 * and /token calls: the bearer rides as the form-encoded `access_token` body parameter
 * (RFC 6750 §2.2) because an Authorization header would force a CORS preflight — which the
 * Functions platform swallows before the app can answer it. A 401 clears the session like every
 * other authenticated call. Requires an admin grant on the tenant or on the internal tenant
 * (403 otherwise).
 */
export async function listTenantUsers(tenant: string): Promise<TenantUserResponse[]> {
  const { issuer } = authConfigFor(window.location.hostname);
  const res = await fetch(`${issuer}/tenants/${encodeURIComponent(tenant)}/users`, {
    method: 'POST',
    body: new URLSearchParams({ access_token: getAccessToken() ?? '' }),
  });
  if (res.status === 401) clearSession();
  await ensureOk(res);
  return (await res.json()) as TenantUserResponse[];
}

/** GET /api/Version — the deployed artifact identity. */
export async function getVersion(): Promise<VersionResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/Version`));
  return (await res.json()) as VersionResponse;
}
